import { supportsMultiCurrencyDisplay } from '../constants/multiCurrencyPaymentMethods';
import { formatCurrencyForDisplay } from './formatCurrencyForDisplay';

export type DisplayAmountKey =
  | 'total'
  | 'order'
  | 'discount'
  | 'fees'
  | 'refund'
  | 'subtotal'
  | 'vat'
  | 'detailsFees'
  | `product-${string}`;

export type IntentionDisplayData = {
  currency?: string;
  amount?: string;
  totalAmount?: string;
  convertedCurrency?: string;
  convertedTotalAmount?: string | null;
  convertedOrderAmount?: string | null;
  convertedDiscountedAmount?: string | null;
  convertedFeesAmount?: string | null;
  convertedRefundFeesAmount?: string | null;
  convertedSubtotal?: string | null;
  convertedVat?: string | null;
  convertedDetailsFeesAmount?: string | null;
  convertedProductAmounts?: Record<string, string> | null;
  multiCurrencyEnabled?: boolean;
};

export type CheckoutDisplayAmount = {
  amount: string;
  currency: string;
  displayCurrency: string;
  currencyClassName: string;
  currencyFontFamily: string;
  isConverted: boolean;
};

const CONVERTED_AMOUNT_BY_KEY: Record<
  Exclude<DisplayAmountKey, `product-${string}`>,
  keyof IntentionDisplayData
> = {
  total: 'convertedTotalAmount',
  order: 'convertedOrderAmount',
  discount: 'convertedDiscountedAmount',
  fees: 'convertedFeesAmount',
  refund: 'convertedRefundFeesAmount',
  subtotal: 'convertedSubtotal',
  vat: 'convertedVat',
  detailsFees: 'convertedDetailsFeesAmount',
};

export function isShowingConvertedCurrency(
  intentionData: IntentionDisplayData,
  paymentMethodType?: string | null
): boolean {
  const merchantCurrency = intentionData.currency || '';

  return (
    supportsMultiCurrencyDisplay(paymentMethodType) &&
    !!intentionData.multiCurrencyEnabled &&
    !!intentionData.convertedCurrency &&
    intentionData.convertedCurrency !== merchantCurrency &&
    !!intentionData.convertedTotalAmount
  );
}

function getConvertedAmount(
  key: DisplayAmountKey,
  intentionData: IntentionDisplayData
): string | null | undefined {
  if (key.startsWith('product-')) {
    return intentionData.convertedProductAmounts?.[key];
  }

  const convertedKey = CONVERTED_AMOUNT_BY_KEY[key as Exclude<DisplayAmountKey, `product-${string}`>];
  const convertedAmount = intentionData[convertedKey];

  return typeof convertedAmount === 'string' ? convertedAmount : null;
}

export function getDisplayAmount(
  key: DisplayAmountKey,
  merchantAmount: string,
  intentionData: IntentionDisplayData,
  paymentMethodType?: string | null
): string {
  if (!isShowingConvertedCurrency(intentionData, paymentMethodType)) {
    return merchantAmount;
  }

  const convertedAmount = getConvertedAmount(key, intentionData);

  return convertedAmount ?? merchantAmount;
}

export function getActiveDisplayCurrency(
  intentionData: IntentionDisplayData,
  paymentMethodType?: string | null
): string {
  const merchantCurrency = intentionData.currency || '';

  if (isShowingConvertedCurrency(intentionData, paymentMethodType)) {
    return intentionData.convertedCurrency!;
  }

  return merchantCurrency;
}

export function getActiveDisplayCurrencyInfo(
  intentionData: IntentionDisplayData,
  paymentMethodType?: string | null
) {
  return formatCurrencyForDisplay(
    getActiveDisplayCurrency(intentionData, paymentMethodType)
  );
}

export function getSelectedConverterCurrency(
  intentionData: IntentionDisplayData,
  multiCurrencyEnabled = false
): string {
  const merchantCurrency = intentionData.currency || '';

  if (!multiCurrencyEnabled) {
    return merchantCurrency;
  }

  return intentionData.convertedCurrency ?? merchantCurrency;
}

export function getCheckoutDisplayAmount(
  intentionData: IntentionDisplayData,
  paymentMethodType?: string | null
): CheckoutDisplayAmount {
  const merchantCurrency = intentionData.currency || '';
  const merchantAmount = intentionData.totalAmount || intentionData.amount || '';
  const isConverted = isShowingConvertedCurrency(
    intentionData,
    paymentMethodType
  );
  const currency = getActiveDisplayCurrency(intentionData, paymentMethodType);
  const amount = getDisplayAmount(
    'total',
    merchantAmount,
    intentionData,
    paymentMethodType
  );

  const { displayCurrency, currencyClassName, currencyFontFamily } =
    formatCurrencyForDisplay(currency);

  return {
    amount,
    currency,
    displayCurrency,
    currencyClassName,
    currencyFontFamily,
    isConverted,
  };
}
